import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import PageHeader from "../../../components/PageHeader";

// Icons
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import EventIcon from "@mui/icons-material/Event";
import PublicIcon from "@mui/icons-material/Public";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";

// --- CONFIGURACIÓN DE COLORES CORPORATIVOS ---
const COLORS = {
  navy: "#2B2B6B",
  red: "#E31E24",
  cyan: "#00CED1",
  orange: "#FF6B35",
};

// --- DATOS ESTÁTICOS ---
const academicPrograms = [
  {
    title: "Business Administration",
    description:
      "Develop managerial and strategic skills to lead organizations in a globalized environment.",
    icon: <BusinessCenterIcon sx={{ fontSize: 40 }} />,
    duration: "2.5 years",
    credits: "72 credits",
    color: COLORS.navy,
    highlights: [
      "Business Management",
      "Corporate Finance",
      "Strategic Marketing",
      "Organizational Leadership",
    ],
  },
  {
    title: "Liberal Arts",
    description:
      "Foster critical thinking and interdisciplinary competencies to face complex challenges of the modern world.",
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    duration: "2 years",
    credits: "70 credits",
    color: COLORS.red,
    highlights: [
      "Critical Thinking",
      "Effective Communication",
      "Cultural Analysis",
      "Academic Research",
    ],
  },
];

const partnerUniversities = [
  {
    name: "University of Central Florida",
    country: "United States",
    programs: "Business, Hospitality, Liberal Arts",
  },
  {
    name: "Humber College",
    country: "Canada",
    programs: "Business, Tourism, Arts",
  },
  {
    name: "University of Westminster",
    country: "United Kingdom",
    programs: "Business, Communications, Arts",
  },
  {
    name: "Universidad Europea",
    country: "Spain",
    programs: "Administration, Sports, Communications",
  },
];

const scholarshipTypes = [
  {
    title: "Academic Scholarship",
    description: "Recognizes exceptional academic performance",
    percentage: "Up to 100%",
    color: COLORS.navy,
    requirements: [
      "Minimum GPA 8.5",
      "Motivation letter",
      "Personal interview",
    ],
  },
  {
    title: "Merit Scholarship",
    description: "Rewards outstanding achievements in specific areas",
    percentage: "Up to 75%",
    color: COLORS.red,
    requirements: ["Verifiable achievements", "Merit essay", "References"],
  },
];



// Encabezado estándar para cada sección
const SectionHeader = ({ title, icon, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
    <Avatar
      sx={{
        backgroundColor: color,
        width: 50,
        height: 50,
        mr: 2,
        color: "#fff",
      }}
    >
      {icon}
    </Avatar>
    <Typography
      variant="h4"
      sx={{
        color: COLORS.navy,
        fontWeight: 700,
        fontSize: { xs: "1.75rem", md: "2rem" },
      }}
    >
      {title}
    </Typography>
  </Box>
);

// Elemento de lista con checkmark
const CheckListItem = ({ text, color = COLORS.navy, small = false }) => (
  <ListItem sx={{ px: 0, py: 0.5 }}>
    <ListItemIcon sx={{ minWidth: 32 }}>
      <CheckCircleOutlineIcon
        sx={{ color: color, fontSize: small ? 16 : 18 }}
      />
    </ListItemIcon>
    <ListItemText
      primary={text}
      primaryTypographyProps={{
        fontSize: small ? "0.85rem" : "0.9rem",
        color: "text.secondary",
      }}
    />
  </ListItem>
);

// Descripción general de sección
const SectionDescription = ({ children }) => (
  <Typography
    variant="body1"
    sx={{
      color: "text.secondary",
      fontSize: "1.05rem",
      lineHeight: 1.8,
      mb: 4,
    }}
  >
    {children}
  </Typography>
);

// --- COMPONENTE PRINCIPAL ---
const ServicesMain = () => {
  const navigate = useNavigate();

  // Referencias para navegación interna
  const refs = {
    academic: useRef(null),
    graduation: useRef(null),
    events: useRef(null),
    international: useRef(null),
    studyAbroad: useRef(null),
    socialService: useRef(null),
    scholarships: useRef(null),
    pathway: useRef(null),
    entrepreneurship: useRef(null),
    wellbeing: useRef(null),
    publicity: useRef(null),
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sidebarSections = [
    {
      id: "academic",
      label: "Academic Programs",
      ref: refs.academic,
      icon: <SchoolIcon />,
    },
    {
      id: "graduation",
      label: "Graduation Seminar",
      ref: refs.graduation,
      icon: <EmojiEventsIcon />,
    },
    { id: "events", label: "Events", ref: refs.events, icon: <EventIcon /> },
    {
      id: "international",
      label: "International Partnerships",
      ref: refs.international,
      icon: <PublicIcon />,
    },
    {
      id: "study-abroad",
      label: "Study Abroad",
      ref: refs.studyAbroad,
      icon: <FlightTakeoffIcon />,
    },
    {
      id: "social-service",
      label: "Social Service",
      ref: refs.socialService,
      icon: <VolunteerActivismIcon />,
    },
    {
      id: "scholarships",
      label: "Scholarship Program",
      ref: refs.scholarships,
      icon: <WorkspacePremiumIcon />,
    },
    {
      id: "pathway",
      label: "Pathway Program",
      ref: refs.pathway,
      icon: <TrendingUpIcon />,
    },
    {
      id: "entrepreneurship",
      label: "Entrepreneurship",
      ref: refs.entrepreneurship,
      icon: <BusinessCenterIcon />,
    },
    {
      id: "wellbeing",
      label: "Student Wellbeing",
      ref: refs.wellbeing,
      icon: <FavoriteIcon />,
    },
    {
      id: "publicity",
      label: "Media & Communications",
      ref: refs.publicity,
      icon: <CampaignIcon />,
    },
  ];

  // Estilos compartidos para tarjetas hover
  const hoverCardStyles = (borderColor) => ({
    border: "1px solid #E5E7EB",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: borderColor,
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px -10px rgba(0,0,0,0.15)",
    },
  });

  return (
    <>
      <PageHeader
        title="Our Services"
        subtitle="Discover all the opportunities and resources that American College has to support your academic and professional development"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "About Us", href: "/about-us" },
          { label: "Services", href: "/about-us/services" },
        ]}
      />

      <Container maxWidth="xl" sx={{ mb: 8, mt: 4 }}>
        <Grid container spacing={4}>
          {/* Sidebar Navigation */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                position: "sticky",
                top: 100,
                border: "1px solid #E5E7EB",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${COLORS.red} 0%, #C41A1F 100%)`,
                  color: "white",
                  p: 2.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "1.1rem", fontWeight: 600 }}
                >
                  Services Directory
                </Typography>
              </Box>
              <List sx={{ py: 1 }}>
                {sidebarSections.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    <ListItemButton
                      onClick={() => scrollToSection(section.ref)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        "&:hover": {
                          backgroundColor: "rgba(227, 30, 36, 0.04)",
                          borderLeft: `4px solid ${COLORS.red}`,
                        },
                        borderLeft: "4px solid transparent",
                        transition: "all 0.2s",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: COLORS.red }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {/* --- 1. ACADEMIC PROGRAMS --- */}
            <Box ref={refs.academic} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <SectionHeader
                title="Academic Programs - Associate Degrees"
                icon={<SchoolIcon />}
                color={COLORS.navy}
              />
              <SectionDescription>
                Our Associate Degree programs are designed with international
                standards, allowing you to transfer credits to prestigious
                universities worldwide or enter the job market directly with
                exceptional preparation.
              </SectionDescription>

              <Grid container spacing={3} alignItems="stretch">
                {academicPrograms.map((program, index) => (
                  <Grid item xs={12} key={index} sx={{ display: "flex" }}>
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        minHeight: { xs: "auto", md: "260px" },
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        ...hoverCardStyles(program.color),
                      }}
                    >
                      <Grid container sx={{ flexGrow: 1 }} alignItems="stretch">
                        <Grid item xs={12} md={3} sx={{ display: "flex" }}>
                          <Box
                            sx={{
                              width: "100%",
                              minHeight: "200px",
                              background: `linear-gradient(135deg, ${program.color} 0%, ${program.color}dd 100%)`,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 3,
                              color: "white",
                            }}
                          >
                            <Box sx={{ mb: 2 }}>{program.icon}</Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                textAlign: "center",
                                mb: 2,
                              }}
                            >
                              {program.title}
                            </Typography>
                            <Chip
                              label={program.duration}
                              sx={{
                                backgroundColor: "rgba(255,255,255,0.2)",
                                color: "white",
                                fontWeight: 600,
                                mb: 1,
                              }}
                            />
                            <Chip
                              label={program.credits}
                              sx={{
                                backgroundColor: "rgba(255,255,255,0.2)",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={9} sx={{ display: "flex" }}>
                          <CardContent
                            sx={{
                              p: 4,
                              flexGrow: 1,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: "text.secondary",
                                mb: 3,
                                lineHeight: 1.8,
                              }}
                            >
                              {program.description}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: program.color,
                                fontWeight: 700,
                                mb: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Focus Areas
                            </Typography>
                            <Grid container spacing={1}>
                              {program.highlights.map((highlight, idx) => (
                                <Grid item xs={12} sm={6} key={idx}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mt: 1,
                                    }}
                                  >
                                    <CheckCircleOutlineIcon
                                      sx={{
                                        color: program.color,
                                        fontSize: 18,
                                        mr: 1,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "text.secondary" }}
                                    >
                                      {highlight}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 2. GRADUATION SEMINAR --- */}
            <Box ref={refs.graduation} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <SectionHeader
                title="Graduation Seminar"
                icon={<EmojiEventsIcon />}
                color={COLORS.red}
              />
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: "1px solid #E5E7EB",
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, rgba(227,30,36,0.02) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  The Graduation Seminar is a capstone course that integrates
                  all the knowledge acquired during your career. This space
                  allows you to develop a final project that demonstrates your
                  professional mastery and prepares you for workplace
                  challenges.
                </Typography>
                <Grid container spacing={3} alignItems="stretch">
                  {[
                    {
                      title: "Integrative Project",
                      description:
                        "Develop a project that integrates all competencies from your program",
                      icon: <BusinessCenterIcon />,
                    },
                    {
                      title: "Professional Mentoring",
                      description:
                        "Receive guidance from specialized professionals and faculty",
                      icon: <SchoolIcon />,
                    },
                    {
                      title: "Final Presentation",
                      description:
                        "Defend your project before a panel of industry experts",
                      icon: <EmojiEventsIcon />,
                    },
                  ].map((item, index) => (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ display: "flex" }}
                      key={index}
                    >
                      <Box
                        sx={{
                          p: 4,
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 2,
                          textAlign: "center",
                          backgroundColor: "#fff",
                          ...hoverCardStyles(COLORS.red),
                        }}
                      >
                        <Avatar
                          sx={{
                            backgroundColor: `${COLORS.red}15`,
                            color: COLORS.red,
                            width: 56,
                            height: 56,
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          {item.icon}
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{
                            color: COLORS.navy,
                            fontWeight: 600,
                            mb: 1,
                            fontSize: "1.1rem",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", flexGrow: 1 }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 3. EVENTS --- */}
            <Box ref={refs.events} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <SectionHeader
                title="Events"
                icon={<EventIcon />}
                color={COLORS.cyan}
              />
              <SectionDescription>
                American College organizes a variety of academic, cultural, and
                social events that enrich your university experience and connect
                you with industry professionals.
              </SectionDescription>
              <Grid container spacing={3} alignItems="stretch">
                {[
                  {
                    title: "Conferences & Seminars",
                    description:
                      "International experts share knowledge about trends and best practices",
                    color: COLORS.navy,
                  },
                  {
                    title: "University Fairs",
                    description:
                      "Meet representatives from our international partner universities",
                    color: COLORS.red,
                  },
                  {
                    title: "Cultural Activities",
                    description:
                      "Celebrations, sports competitions, and artistic presentations",
                    color: COLORS.cyan,
                  },
                  {
                    title: "Professional Networking",
                    description:
                      "Connect with companies, employers, and successful alumni",
                    color: COLORS.orange,
                  },
                ].map((event, index) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex" }}
                    key={index}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        ...hoverCardStyles(event.color),
                      }}
                    >
                      <Box
                        sx={{
                          height: "4px",
                          background: `linear-gradient(90deg, ${event.color} 0%, ${event.color}80 100%)`,
                        }}
                      />
                      <CardContent
                        sx={{
                          p: 4,
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: event.color,
                            fontWeight: 700,
                            mb: 1,
                            fontSize: "1.15rem",
                          }}
                        >
                          {event.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", lineHeight: 1.7 }}
                        >
                          {event.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 4. INTERNATIONAL PARTNERSHIPS --- */}
            <Box
              ref={refs.international}
              sx={{ mb: 8, scrollMarginTop: "100px" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <SectionHeader
                  title="International Partnerships"
                  icon={<PublicIcon />}
                  color={COLORS.navy}
                />
                <Button
                  variant="outlined"
                  onClick={() => navigate("/about-us/map")}
                  sx={{
                    borderColor: COLORS.navy,
                    color: COLORS.navy,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: `${COLORS.navy}05`,
                      borderColor: COLORS.navy,
                    },
                  }}
                >
                  See more
                </Button>
              </Box>

              <SectionDescription>
                Our international partnerships allow you to transfer credits and
                continue your studies at prestigious universities around the
                world, facilitating your global academic development.
              </SectionDescription>

              <Grid container spacing={3} alignItems="stretch">
                {partnerUniversities.map((university, index) => (
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex" }}
                    key={index}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        minHeight: "210px",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        ...hoverCardStyles(COLORS.navy),
                      }}
                    >
                      <CardContent sx={{ p: 3, flexGrow: 1 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <PublicIcon
                            sx={{ color: COLORS.navy, fontSize: 32, mr: 2 }}
                          />
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                color: COLORS.navy,
                                fontWeight: 700,
                                fontSize: "1rem",
                                mb: 0.5,
                                lineHeight: 1.2,
                              }}
                            >
                              {university.name}
                            </Typography>
                            <Chip
                              label={university.country}
                              size="small"
                              sx={{
                                backgroundColor: `${COLORS.red}15`,
                                color: COLORS.red,
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            />
                          </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", lineHeight: 1.7 }}
                        >
                          <strong>Programs:</strong> {university.programs}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  backgroundColor: "#F8FAFC",
                  borderRadius: 2,
                  border: "1px dashed #CBD5E1",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center" }}
                >
                  <InfoIcon
                    sx={{
                      fontSize: 20,
                      verticalAlign: "middle",
                      mr: 1,
                      color: COLORS.navy,
                    }}
                  />
                  We have partnerships with over 15 universities in the US,
                  Canada, Europe, and Asia.
                </Typography>
              </Box>
            </Box>

            {/* --- 5. STUDY ABROAD --- */}
            <Box
              ref={refs.studyAbroad}
              sx={{ mb: 8, scrollMarginTop: "100px" }}
            >
              <SectionHeader
                title="Study Abroad"
                icon={<FlightTakeoffIcon />}
                color={COLORS.red}
              />
              <SectionDescription>
                We accompany you in every step of your study abroad process,
                from university selection to your arrival in the destination
                country.
              </SectionDescription>
              <Grid container spacing={3} alignItems="stretch">
                {[
                  {
                    title: "Study Recommendations",
                    color: COLORS.navy,
                    items: [
                      "Research accredited universities",
                      "Prepare in advance",
                      "Improve your English level",
                      "Organize your finances",
                      "Learn about the culture",
                    ],
                  },
                  {
                    title: "Admission Requirements",
                    color: COLORS.red,
                    items: [
                      "Academic transcripts",
                      "Language tests (TOEFL/IELTS)",
                      "Motivation letter",
                      "Letters of recommendation",
                      "Standardized tests (SAT/GRE)",
                    ],
                  },
                  {
                    title: "Living Abroad",
                    color: COLORS.cyan,
                    items: [
                      "Housing and accommodation",
                      "Estimated cost of living",
                      "International health insurance",
                      "Transportation and mobility",
                      "Cultural adaptation",
                    ],
                  },
                ].map((block, idx) => (
                  <Grid item xs={12} md={4} sx={{ display: "flex" }} key={idx}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${block.color}`,
                        borderRadius: 2,
                        ...hoverCardStyles(block.color),
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: block.color, fontWeight: 700, mb: 2 }}
                      >
                        {block.title}
                      </Typography>
                      <List sx={{ p: 0, flexGrow: 1 }}>
                        {block.items.map((item, i) => (
                          <CheckListItem
                            key={i}
                            text={item}
                            color={block.color}
                          />
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 6. SOCIAL SERVICE --- */}
            <Box
              ref={refs.socialService}
              sx={{ mb: 8, scrollMarginTop: "100px" }}
            >
              <SectionHeader
                title="Social Service (Internships & Community)"
                icon={<VolunteerActivismIcon />}
                color={COLORS.cyan}
              />
              <Paper
                elevation={0}
                sx={{ p: 4, border: "1px solid #E5E7EB", borderRadius: 3 }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  Social service is a fundamental part of your education at
                  American College. Through internships and community hours, you
                  develop professional skills while contributing positively to
                  your community.
                </Typography>
                <Grid container spacing={3} alignItems="stretch">
                  <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                    <Box
                      sx={{
                        p: 4,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: `${COLORS.cyan}10`,
                        borderRadius: 2,
                        borderLeft: `4px solid ${COLORS.cyan}`,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: COLORS.cyan, fontWeight: 700, mb: 2 }}
                      >
                        Professional Internships
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mb: 3, flexGrow: 1 }}
                      >
                        Gain practical experience in companies and institutions
                        related to your field of study.
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {[
                          "Minimum 100 hours",
                          "Academic supervision",
                          "Final activity report",
                          "Performance evaluation",
                        ].map((item, idx) => (
                          <CheckListItem
                            key={idx}
                            text={item}
                            color={COLORS.cyan}
                            small
                          />
                        ))}
                      </List>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                    <Box
                      sx={{
                        p: 4,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: `${COLORS.navy}10`,
                        borderRadius: 2,
                        borderLeft: `4px solid ${COLORS.navy}`,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: COLORS.navy, fontWeight: 700, mb: 2 }}
                      >
                        Community Hours
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mb: 3, flexGrow: 1 }}
                      >
                        Contribute to community development projects and social
                        responsibility.
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {[
                          "Community projects",
                          "NGOs and foundations",
                          "Educational institutions",
                          "Environmental programs",
                        ].map((item, idx) => (
                          <CheckListItem
                            key={idx}
                            text={item}
                            color={COLORS.navy}
                            small
                          />
                        ))}
                      </List>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 7. SCHOLARSHIP PROGRAM --- */}
            <Box
              ref={refs.scholarships}
              sx={{ mb: 8, scrollMarginTop: "100px" }}
            >
              <SectionHeader
                title="Scholarship Program"
                icon={<WorkspacePremiumIcon />}
                color={COLORS.orange}
              />
              <SectionDescription>
                American College recognizes the talent and effort of its
                students by offering various types of scholarships that can
                cover up to 100% of your tuition.
              </SectionDescription>
              <Grid container spacing={3} alignItems="stretch">
                {scholarshipTypes.map((scholarship, index) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex" }}
                    key={index}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        ...hoverCardStyles(scholarship.color),
                      }}
                    >
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${scholarship.color} 0%, ${scholarship.color}dd 100%)`,
                          p: 3,
                          color: "white",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, fontSize: "1.1rem" }}
                        >
                          {scholarship.title}
                        </Typography>
                        <Chip
                          label={scholarship.percentage}
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.25)",
                            color: "white",
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                      <CardContent
                        sx={{
                          p: 4,
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.secondary",
                            mb: 4,
                            lineHeight: 1.7,
                            flexGrow: 1,
                          }}
                        >
                          {scholarship.description}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: scholarship.color,
                            fontWeight: 700,
                            mb: 1.5,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Requirements
                        </Typography>
                        <List sx={{ p: 0 }}>
                          {scholarship.requirements.map((req, idx) => (
                            <CheckListItem
                              key={idx}
                              text={req}
                              color={scholarship.color}
                            />
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 5, textAlign: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/about-us/admissions")}
                  sx={{
                    backgroundColor: COLORS.orange,
                    color: "white",
                    px: 5,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "#E85A2C" },
                  }}
                >
                  Apply for Scholarship
                </Button>
              </Box>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 8. PATHWAY PROGRAM --- */}
            <Box ref={refs.pathway} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <SectionHeader
                title="Pathway Program"
                icon={<TrendingUpIcon />}
                color={COLORS.navy}
              />
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  border: `1px solid ${COLORS.navy}`,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${COLORS.navy}08 0%, rgba(255,255,255,1) 100%)`,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 5,
                  }}
                >
                  The Pathway Program is your bridge to prestigious
                  international universities. This specialized program prepares
                  you academically, linguistically, and culturally for a
                  successful transition to higher education abroad.
                </Typography>
                <Grid container spacing={4} alignItems="stretch">
                  {[
                    {
                      title: "Academic Preparation",
                      items: [
                        "International leveling courses",
                        "University study methodology",
                        "Research skills development",
                        "Advanced academic writing",
                      ],
                      color: COLORS.navy,
                    },
                    {
                      title: "Language Preparation",
                      items: [
                        "Intensive academic English",
                        "TOEFL/IELTS preparation",
                        "Specialized vocabulary",
                        "Professional communication",
                      ],
                      color: COLORS.red,
                    },
                    {
                      title: "Guidance & Advising",
                      items: [
                        "University selection",
                        "Application support",
                        "Interview preparation",
                        "Visa and travel procedures",
                      ],
                      color: COLORS.cyan,
                    },
                  ].map((section, index) => (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ display: "flex" }}
                      key={index}
                    >
                      <Box
                        sx={{
                          p: 4,
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: "#fff",
                          border: `1px solid ${section.color}40`,
                          borderTop: `4px solid ${section.color}`,
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: section.color,
                            fontWeight: 700,
                            mb: 3,
                            fontSize: "1.1rem",
                          }}
                        >
                          {section.title}
                        </Typography>
                        <List sx={{ p: 0, flexGrow: 1 }}>
                          {section.items.map((item, idx) => (
                            <CheckListItem
                              key={idx}
                              text={item}
                              color={section.color}
                            />
                          ))}
                        </List>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Box
                  sx={{
                    mt: 5,
                    p: 4,
                    backgroundColor: `${COLORS.navy}10`,
                    borderRadius: 2,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: COLORS.navy, fontWeight: 700, mb: 1 }}
                  >
                    Program Success Rate
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{ color: COLORS.red, fontWeight: 800, mb: 1 }}
                  >
                    95%
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    of our Pathway students achieve admission to their target
                    universities
                  </Typography>
                </Box>
              </Paper>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 9. ENTREPRENEURSHIP --- */}
            <Box
              ref={refs.entrepreneurship}
              sx={{ mb: 8, scrollMarginTop: "100px" }}
            >
              <SectionHeader
                title="Entrepreneurship Program"
                icon={<BusinessCenterIcon />}
                color={COLORS.red}
              />
              <SectionDescription>
                Develop your entrepreneurial spirit and learn to create, manage,
                and grow your own business with the support of experienced
                mentors and specialized resources.
              </SectionDescription>
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 5 },
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      ...hoverCardStyles(COLORS.red),
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: COLORS.red,
                        fontWeight: 700,
                        mb: 4,
                        fontSize: "1.25rem",
                      }}
                    >
                      What Does the Program Include?
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      sx={{ flexGrow: 1, alignContent: "flex-start" }}
                    >
                      {[
                        "Business idea development workshops",
                        "Mentorship with successful entrepreneurs",
                        "Access to incubators and accelerators",
                        "Business plan development support",
                        "Investor networking",
                        "Pitch and presentation training",
                        "Legal and tax advisory",
                        "Connection with investment funds",
                      ].map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              mt: 1,
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{
                                color: COLORS.red,
                                fontSize: 20,
                                mr: 1.5,
                                mt: 0.2,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", lineHeight: 1.6 }}
                            >
                              {item}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      p: 4,
                      width: "100%",
                      background: `linear-gradient(135deg, ${COLORS.red} 0%, #C41A1F 100%)`,
                      borderRadius: 3,
                      color: "white",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      textAlign: "center",
                      boxShadow: "0 10px 25px -5px rgba(227,30,36,0.3)",
                    }}
                  >
                    <BusinessCenterIcon
                      sx={{ fontSize: 50, mb: 3, mx: "auto", opacity: 0.9 }}
                    />
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                      50+
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                      Startups created by our students
                    </Typography>
                    <Divider
                      sx={{ borderColor: "rgba(255,255,255,0.2)", mb: 4 }}
                    />
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                      $2M+
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Investment secured by alumni
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 10. STUDENT WELLBEING --- */}
            <Box ref={refs.wellbeing} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <SectionHeader
                title="Student Wellbeing"
                icon={<FavoriteIcon />}
                color={COLORS.cyan}
              />
              <SectionDescription>
                Your holistic wellbeing is our priority. We offer various
                support services for your personal, emotional, and social
                development during your university life.
              </SectionDescription>
              <Grid container spacing={3} alignItems="stretch">
                {[
                  {
                    title: "Psychological Counseling",
                    description:
                      "Confidential emotional support and professional guidance",
                    icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
                    color: COLORS.red,
                  },
                  {
                    title: "Sports Activities",
                    description:
                      "Tournaments, university teams, and gym facilities",
                    icon: <EmojiEventsIcon sx={{ fontSize: 32 }} />,
                    color: COLORS.navy,
                  },
                  {
                    title: "Clubs & Organizations",
                    description: "Student groups for diverse interests",
                    icon: <EventIcon sx={{ fontSize: 32 }} />,
                    color: COLORS.cyan,
                  },
                  {
                    title: "Career Guidance",
                    description: "Guidance for your professional development",
                    icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
                    color: COLORS.orange,
                  },
                ].map((service, index) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex" }}
                    key={index}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        ...hoverCardStyles(service.color),
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 4,
                          flexGrow: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          sx={{
                            backgroundColor: `${service.color}15`,
                            color: service.color,
                            width: 64,
                            height: 64,
                            mr: 3,
                          }}
                        >
                          {service.icon}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color: service.color,
                              fontWeight: 700,
                              fontSize: "1.1rem",
                              mb: 0.5,
                            }}
                          >
                            {service.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", lineHeight: 1.6 }}
                          >
                            {service.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Divider sx={{ my: 6 }} />

            {/* --- 11. MEDIA & COMMUNICATIONS --- */}
            <Box ref={refs.publicity} sx={{ mb: 6, scrollMarginTop: "100px" }}>
              <SectionHeader
                title="Media & Communications"
                icon={<CampaignIcon />}
                color={COLORS.orange}
              />
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 3,
                  ...hoverCardStyles(COLORS.orange),
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 5,
                  }}
                >
                  Our Media and Communications department keeps the community
                  informed about institutional achievements, events, and
                  opportunities. It also supports student communication and
                  marketing projects.
                </Typography>
                <Grid container spacing={3}>
                  {[
                    "Quarterly institutional magazine",
                    "Active social media presence",
                    "Newsletter for students and alumni",
                    "Event coverage",
                    "Student project support",
                    "Press releases",
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <CheckCircleOutlineIcon
                          sx={{
                            color: COLORS.orange,
                            fontSize: 20,
                            mr: 1.5,
                            mt: 0.2,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", lineHeight: 1.5 }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>

            {/* --- CALL TO ACTION --- */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                background: `linear-gradient(135deg, ${COLORS.red} 0%, #C41A1F 100%)`,
                color: "white",
                borderRadius: 3,
                textAlign: "center",
                mt: 8,
                boxShadow: "0 10px 30px -10px rgba(227,30,36,0.4)",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Need More Information About Our Services?
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}
              >
                Contact us and we'll gladly answer all your questions
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/contact")}
                sx={{
                  backgroundColor: "white",
                  color: COLORS.red,
                  px: 6,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#F8FAFC",
                    transform: "scale(1.02)",
                  },
                  transition: "all 0.2s",
                }}
              >
                Contact Us
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default ServicesMain;
